import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import { PageViewer } from '../../components/PageViewer';
import {
  PATH_MATERIAL_GENERAL,
  PATH_MATERIAL_GENERAL_EDIT,
} from '../../app/constants/paths.constant';
import { FormVariant } from '../../forms';
import { MaterialForm, MaterialFormTab } from '../../forms/Material';
import { Code } from '../../components/Code';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';

export const MaterialPage_GeneralInfo_Material_Fragment = graphql(`
  fragment MaterialPage_GeneralInfo_Material_Fragment on Material {
    _id
    codingConfig {
      kind
      strategyId
      ... on CodingConfigManual {
        code
      }
      ...CodeCmp_CodingConfig_Fragment
    }
    code
    label
    title
    content
    ...MaterialForm_Material_Fragment
  }
`);

interface Props {
  readonly material: FragmentType<
    typeof MaterialPage_GeneralInfo_Material_Fragment
  >;
}

export const GeneralInfo = ({ material: pMaterial }: Readonly<Props>) => {
  const material = getFragmentData(
    MaterialPage_GeneralInfo_Material_Fragment,
    pMaterial,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_MATERIAL_GENERAL,
      edit: PATH_MATERIAL_GENERAL_EDIT,
    },
    { id: material._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  return (
    <>
      <Stack spacing={2} sx={{ pt: 4, width: '100%' }}>
        <Typography gutterBottom variant="h6">
          Matériel
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Code code={material.code} codingConfig={material.codingConfig} />
            <Typography gutterBottom variant="body1">
              Libellé : {material.label}
            </Typography>
          </Box>
        </Paper>
        <Typography gutterBottom variant="h6">
          Document
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            {/*eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing*/}
            {material.title || (
              <Typography gutterBottom variant="body1">
                Titre non renseigné
              </Typography>
            )}
          </Box>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            {material.content ? (
              <PageViewer html={material.content} />
            ) : (
              <Typography gutterBottom variant="body1">
                Contenu non renseigné
              </Typography>
            )}
          </Box>
        </Paper>
      </Stack>
      <SpeedDial
        actions={[
          {
            id: 'editMaterialGeneralInfo',
            icon: <EditIcon />,
            name: 'Modifier Matériel',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Informations Générales du Matériel"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <MaterialForm
          formVariant={FormVariant.Modification}
          initialValues={material}
          materialId={material._id}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          stepIdsSubset={[MaterialFormTab.MaterialInfo]}
          title="Formulaire de modification des Informations Générales du Matériel"
        />
      </RightDrawer>
    </>
  );
};
